import React, { Component } from "react";
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faEdit, faSearch, faChevronDown, faChevronUp, faLink, faTimes, faEnvelope, faUser, faShieldAlt, faKey, faLock } from '@fortawesome/free-solid-svg-icons';
import { getUsers, createUser, updateUser, deleteUser, lookupUserByEmail, assignVaultToUser, removeVaultFromUser, getVaults } from '../api/backend';
import { Application } from '../core/app';
import { escapeHtml } from '../utils/sanitize';
import { showToast } from '../toast';

const styles = {
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  pageTitle: { color: '#ffffff', margin: 0, fontSize: '24px', fontWeight: 600 },
  headerActions: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  searchInput: { backgroundColor: '#2d2d2d', border: '1px solid #424242', borderRadius: '8px', color: '#ffffff', padding: '10px 14px', fontSize: '14px', width: '260px', outline: 'none' },
  btnPrimary: { display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', backgroundColor: '#4caf50', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap' },
  btnSecondary: { display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', backgroundColor: '#2196f3', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap' },
  btnLookup: { display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', backgroundColor: '#7c4dff', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap' },
  userCard: { backgroundColor: '#252525', borderRadius: '12px', marginBottom: '12px', border: '1px solid #333333', overflow: 'hidden' },
  userCardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', cursor: 'pointer', gap: '12px' },
  userCardIdentity: { display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 },
  userAvatar: { width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#424242', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9e9e9e', fontSize: '16px', flexShrink: 0 },
  userName: { color: '#ffffff', fontSize: '15px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  userEmail: { color: '#9e9e9e', fontSize: '13px', marginTop: '2px' },
  userBadges: { display: 'flex', gap: '6px', flexShrink: 0 },
  badge: (color) => ({ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, backgroundColor: color + '22', color: color, textTransform: 'capitalize', whiteSpace: 'nowrap' }),
  cardActions: { display: 'flex', gap: '4px', flexShrink: 0 },
  iconBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#9e9e9e', backgroundColor: 'transparent', transition: 'all 0.15s' },
  iconBtnEdit: { color: '#2196f3' },
  iconBtnDelete: { color: '#f44336' },
  iconBtnAssign: { color: '#4caf50' },
  userCardDetails: { padding: '0 20px 16px 20px', borderTop: '1px solid #333333' },
  detailsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', padding: '16px 0' },
  detailItem: { display: 'flex', flexDirection: 'column', gap: '2px' },
  detailLabel: { color: '#757575', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' },
  detailValue: { color: '#e0e0e0', fontSize: '13px', wordBreak: 'break-word' },
  vaultChips: { display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' },
  vaultChip: { backgroundColor: '#37474f', color: '#b0bec5', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', display: 'inline-flex', alignItems: 'center', gap: '4px' },
  vaultChipRemove: { background: 'none', border: 'none', color: '#ef5350', cursor: 'pointer', padding: '0', fontSize: '12px', lineHeight: 1 },
  dialogForm: { display: 'flex', flexDirection: 'column', minWidth: '320px', maxWidth: '500px' },
  formGroup: { marginBottom: '16px' },
  formLabel: { color: '#9e9e9e', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px', display: 'block' },
  formInput: { width: '100%', backgroundColor: '#2d2d2d', border: '1px solid #424242', borderRadius: '8px', color: '#ffffff', padding: '10px 14px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  formSelect: { width: '100%', backgroundColor: '#2d2d2d', border: '1px solid #424242', borderRadius: '8px', color: '#ffffff', padding: '10px 14px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  vaultPicker: { maxHeight: '180px', overflowY: 'auto', backgroundColor: '#1e1e1e', borderRadius: '8px', border: '1px solid #424242', padding: '8px' },
  vaultOption: { display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', borderRadius: '4px', cursor: 'pointer', color: '#e0e0e0', fontSize: '13px' },
  checkboxToggle: { display: 'flex', alignItems: 'center', gap: '10px', color: '#e0e0e0', fontSize: '14px', cursor: 'pointer' },
  lookupResult: (type) => ({ backgroundColor: type === 'found' ? '#1b3616' : type === 'created' ? '#3e2c14' : '#3e1414', padding: '14px', borderRadius: '8px', marginTop: '12px', border: `1px solid ${type === 'found' ? '#2e7d32' : type === 'created' ? '#e65100' : '#c62828'}` }),
  lookupResultTitle: (type) => ({ fontWeight: 600, marginBottom: '8px', color: type === 'found' ? '#4caf50' : type === 'created' ? '#ff9800' : '#f44336', fontSize: '14px' }),
  lookupResultRow: { color: '#b0bec5', fontSize: '13px', marginBottom: '4px' },
  emptyState: { textAlign: 'center', padding: '60px 20px', color: '#757575' },
};

class UserManagement extends Component {
  constructor(props) {
    super(props);
    this.mounted = false;
    this.apisCalled = false;
  }

  state = {
      users: [],
      vaults: [],
      showCreateModal: false,
      showEditModal: false,
      showDeleteModal: false,
      showAssignVaultModal: false,
      showLookupModal: false,
      userToDelete: null,
      editingUser: null,
      assigningUser: null,
      selectedVaultToAssign: '',
      expandedCards: new Set(),
      newUser: { name: '', email: '', password: '', vaults: '', assigned_vault_ids: [], selected_vault_id: '', selected_shopping_list_id: '', allowed: true, role: 'user' },
      isLoading: false,
      searchFilter: '',
      lookupEmail: '',
      lookupResult: null,
      lookupCreating: false,
      lookupError: null
  }

  componentDidMount() {
      if (Application.getAccessToken() && !this.apisCalled) {
          this.apisCalled = true;
          this.mounted = true;
          this.setState({ isLoading: true });
          this.getUsers();
          this.getVaults();
      }
  }

  componentWillUnmount() {
      this.mounted = false;
  }

  getUsers = () => {
      getUsers().then(res => { this.setState({ users: res.data }); this.checkLoadingComplete(); }).catch(() => { this.checkLoadingComplete(); });
  }

  getVaults = () => {
      getVaults().then(res => { this.setState({ vaults: res.data }); this.checkLoadingComplete(); }).catch(() => { this.checkLoadingComplete(); });
  }

  checkLoadingComplete = () => {
      if (this.state.users.length >= 0 && this.state.vaults.length >= 0) this.setState({ isLoading: false });
  }

  toggleCard = (userId) => {
      this.setState(prev => {
          const next = new Set(prev.expandedCards);
          if (next.has(userId)) next.delete(userId); else next.add(userId);
          return { expandedCards: next };
      });
  }

  handleCreateOpen = () => {
      this.setState({ showCreateModal: true, newUser: { name: '', email: '', password: '', vaults: '', assigned_vault_ids: [], selected_vault_id: '', selected_shopping_list_id: '', allowed: false, role: 'user' } });
  }

  handleCreateClose = () => {
      this.setState({ showCreateModal: false, newUser: { name: '', email: '', password: '', vaults: '', assigned_vault_ids: [], selected_vault_id: '', selected_shopping_list_id: '', allowed: false, role: 'user' } });
  }

  handleEditOpen = (user) => {
      this.setState({ showEditModal: true, editingUser: { ...user, assigned_vault_ids: (user.assigned_vault_ids || []).map((id) => String(id)) } });
  }

  handleEditClose = () => { this.setState({ showEditModal: false, editingUser: null }); }

  handleDeleteOpen = (user) => { this.setState({ showDeleteModal: true, userToDelete: user }); }

  handleDeleteClose = () => { this.setState({ showDeleteModal: false, userToDelete: null }); }

  handleAssignVaultOpen = (user) => { this.setState({ showAssignVaultModal: true, assigningUser: user, selectedVaultToAssign: '' }); }

  handleAssignVaultClose = () => { this.setState({ showAssignVaultModal: false, assigningUser: null, selectedVaultToAssign: '' }); }

  handleAssignVault = (vaultId) => {
      if (this.state.assigningUser && vaultId) {
          this.setState({ isLoading: true });
          assignVaultToUser({ vault_id: vaultId, target_user_id: this.state.assigningUser.id }).then(res => { this.getUsers(); this.handleAssignVaultClose(); }).catch(() => { this.setState({ isLoading: false }); });
      }
  }

  handleRemoveVault = (vaultId, userId) => {
      this.setState({ isLoading: true });
      removeVaultFromUser({ vault_id: vaultId, target_user_id: userId }).then(res => { this.getUsers(); }).catch(() => { this.setState({ isLoading: false }); });
  }

  handleCreateUser = () => {
      if (this.state.newUser.email.trim()) {
          const uid = crypto.randomUUID();
          this.setState({ isLoading: true });
          const payload = { ...this.state.newUser, uid, email: this.state.newUser.email };
          if (!payload.password) delete payload.password;
          createUser(payload).then(res => {
              this.getUsers();
              this.handleCreateClose();
              showToast('User created successfully', { severity: 'success' });
          }).catch(err => {
              this.setState({ isLoading: false });
              showToast(err.response?.data?.message || 'Failed to create user', { severity: 'error' });
          });
      }
  }

  handleUpdateUser = () => {
      if (this.state.editingUser) {
          this.setState({ isLoading: true });
          const { created_at, updated_at, assigned_vault_names, assigned_vaults, assignedVaults, ...updateData } = this.state.editingUser;
          updateData.vaults = null;
          if (!updateData.password) delete updateData.password;
          updateUser(updateData).then(res => {
              this.getUsers();
              this.handleEditClose();
              showToast('User updated successfully', { severity: 'success' });
          }).catch(err => {
              this.setState({ isLoading: false });
              showToast(err.response?.data?.message || 'Failed to update user', { severity: 'error' });
          });
      }
  }

  handleDeleteUser = () => {
      if (this.state.userToDelete) {
          this.setState({ isLoading: true, showDeleteModal: false });
          deleteUser({ id: this.state.userToDelete.id }).then(res => {
              this.getUsers();
              showToast('User deleted successfully', { severity: 'success' });
          }).catch(err => {
              this.setState({ isLoading: false });
              showToast(err.response?.data?.message || 'Failed to delete user', { severity: 'error' });
          });
          this.setState({ userToDelete: null });
      }
  }

  handleLookupOpen = () => {
      this.setState({ showLookupModal: true, lookupEmail: '', lookupResult: null, lookupCreating: false, lookupError: null });
  }

  handleLookupClose = () => { this.setState({ showLookupModal: false, lookupResult: null, lookupError: null }); }

  handleLookupSearch = () => {
      const email = this.state.lookupEmail.trim();
      if (!email) return;
      this.setState({ lookupResult: null, lookupError: null });
      lookupUserByEmail({ email, create: false }).then(res => {
          this.setState({ lookupResult: res.data, lookupError: null });
      }).catch(err => {
          this.setState({ lookupError: err.response?.data?.message || 'Lookup failed' });
          showToast(err.response?.data?.message || 'Lookup failed', { severity: 'error' });
      });
  }

  handleLookupCreate = () => {
      const email = this.state.lookupEmail.trim();
      if (!email) return;
      this.setState({ lookupResult: null, lookupError: null });
      lookupUserByEmail({ email, create: true, allowed: this.state.lookupCreating }).then(res => {
          this.setState({ lookupResult: res.data, lookupError: null });
          this.getUsers();
          showToast('User created successfully', { severity: 'success' });
      }).catch(err => {
          this.setState({ lookupError: err.response?.data?.message || 'Failed to create user' });
          showToast(err.response?.data?.message || 'Failed to create user', { severity: 'error' });
      });
  }

  handleSearchFilterChange = (value) => { this.setState({ searchFilter: value }); }

  handleInputChange = (field, value) => { this.setState({ newUser: { ...this.state.newUser, [field]: value } }); }

  handleNewUserVaultToggle = (vaultId, checked) => {
    const vaultIds = new Set((this.state.newUser.assigned_vault_ids || []).map((id) => String(id)));
    if (checked) vaultIds.add(String(vaultId)); else vaultIds.delete(String(vaultId));
    this.handleInputChange('assigned_vault_ids', Array.from(vaultIds));
  }

  handleEditInputChange = (field, value) => { this.setState({ editingUser: { ...this.state.editingUser, [field]: value } }); }

  handleEditVaultToggle = (vaultId, checked) => {
      const vaultIds = new Set((this.state.editingUser?.assigned_vault_ids || []).map((id) => String(id)));
      if (checked) vaultIds.add(String(vaultId)); else vaultIds.delete(String(vaultId));
      this.handleEditInputChange('assigned_vault_ids', Array.from(vaultIds));
  }

  isAdmin = () => {
      const userData = Application.getAuthUserData();
      return userData && userData.role === 'admin';
  }

  getInitials = (name) => {
      if (!name) return '?';
      return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
  }

  formatVaultNames = (value) => {
      if (!value) return 'None';
      const id = String(value);
      const vault = this.state.vaults.find((v) => String(v.id) === id);
      return vault ? vault.name : id;
  }

  renderVaultPicker = (vaultIds, onToggle) => {
      if (!this.state.vaults.length) return <div style={{ color: '#757575', fontSize: '13px', padding: '8px' }}>No vaults available</div>;
      return (
          <div style={styles.vaultPicker}>
              {this.state.vaults.map((vault) => (
                  <label key={vault.id} style={styles.vaultOption}>
                      <input type="checkbox" checked={(vaultIds || []).includes(String(vault.id))}
                        onChange={(e) => onToggle(vault.id, e.target.checked)} style={{ accentColor: '#4caf50' }} />
                      <span>{escapeHtml(vault.name)}</span>
                  </label>
              ))}
          </div>
      );
  }

  renderUserCard = (user) => {
      const expanded = this.state.expandedCards.has(user.id);
      const vaultNames = user.assigned_vault_names || [];
      const vaultIds = user.assigned_vault_ids || [];

      return (
          <div key={user.id} style={styles.userCard}>
              <div style={styles.userCardHeader} onClick={() => this.toggleCard(user.id)}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#2a2a2a'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                  <div style={styles.userCardIdentity}>
                      <div style={styles.userAvatar}>{this.getInitials(user.name)}</div>
                      <div>
                          <div style={styles.userName}>{escapeHtml(user.name || 'Unnamed User')}</div>
                          <div style={styles.userEmail}>
                              {user.email
                                  ? <><FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '4px', fontSize: '11px' }} />{escapeHtml(user.email)}</>
                                  : <span style={{ fontStyle: 'italic', opacity: 0.5 }}>No email set</span>}
                          </div>
                      </div>
                  </div>
                  <div style={styles.userBadges}>
                      <span style={styles.badge(user.role === 'admin' ? '#ff9800' : '#2196f3')}>
                          <FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: '3px', fontSize: '10px' }} />{user.role || 'user'}
                      </span>
                      <span style={styles.badge(user.allowed ? '#4caf50' : '#f44336')}>{user.allowed ? 'Active' : 'Pending'}</span>
                  </div>
                  <div style={styles.cardActions}>
                      <button style={{ ...styles.iconBtn, ...styles.iconBtnAssign }} onClick={(e) => { e.stopPropagation(); this.handleAssignVaultOpen(user); }} title="Assign Vault"
                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#4caf5022'; }} onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                          <FontAwesomeIcon icon={faLink} />
                      </button>
                      <button style={{ ...styles.iconBtn, ...styles.iconBtnEdit }} onClick={(e) => { e.stopPropagation(); this.handleEditOpen(user); }} title="Edit User"
                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#2196f322'; }} onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                          <FontAwesomeIcon icon={faEdit} />
                      </button>
                      {this.isAdmin() && (
                          <button style={{ ...styles.iconBtn, ...styles.iconBtnDelete }} onClick={(e) => { e.stopPropagation(); this.handleDeleteOpen(user); }} title="Delete User"
                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f4433622'; }} onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                              <FontAwesomeIcon icon={faTrash} />
                          </button>
                      )}
                      <button style={styles.iconBtn} onClick={(e) => { e.stopPropagation(); this.toggleCard(user.id); }} title={expanded ? "Collapse" : "Expand"}>
                          <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
                      </button>
                  </div>
              </div>
              {expanded && (
                  <div style={styles.userCardDetails}>
                      <div style={styles.detailsGrid}>
                          <div style={styles.detailItem}>
                              <span style={styles.detailLabel}>Auth UID</span>
                              <span style={{ ...styles.detailValue, fontFamily: 'monospace', fontSize: '12px' }}>{escapeHtml(user.uid)}</span>
                          </div>
                          <div style={styles.detailItem}>
                              <span style={styles.detailLabel}>Selected Vault</span>
                              <span style={styles.detailValue}>{user.selected_vault_id ? this.formatVaultNames(String(user.selected_vault_id)) : 'None'}</span>
                          </div>
                          <div style={styles.detailItem}>
                              <span style={styles.detailLabel}>Shopping List</span>
                              <span style={styles.detailValue}>{user.selected_shopping_list_id || 'None'}</span>
                          </div>
                          <div style={styles.detailItem}>
                              <span style={styles.detailLabel}>Auth Provider</span>
                              <span style={styles.detailValue}>{user.auth_provider ? escapeHtml(user.auth_provider) : '—'}</span>
                          </div>
                          <div style={styles.detailItem}>
                              <span style={styles.detailLabel}>Created</span>
                              <span style={styles.detailValue}>{user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}</span>
                          </div>
                      </div>
                      <div style={{ ...styles.detailItem, marginTop: '8px' }}>
                          <span style={styles.detailLabel}>Assigned Vaults ({vaultNames.length})</span>
                          <div style={styles.vaultChips}>
                              {vaultNames.length === 0
                                  ? <span style={{ color: '#757575', fontSize: '12px' }}>None</span>
                                  : vaultNames.map((name, i) => (
                                      <span key={`${vaultIds[i]}-${i}`} style={styles.vaultChip}>
                                          {escapeHtml(name)}
                                          {vaultIds[i] && <button style={styles.vaultChipRemove} onClick={() => this.handleRemoveVault(vaultIds[i], user.id)}><FontAwesomeIcon icon={faTimes} style={{ fontSize: '10px' }} /></button>}
                                      </span>
                                  ))}
                          </div>
                      </div>
                  </div>
              )}
          </div>
      );
  }

  render() {
    const filteredUsers = this.state.users.filter(user => {
        if (!this.state.searchFilter) return true;
        const f = this.state.searchFilter.toLowerCase();
        return (user.name || '').toLowerCase().includes(f) || (user.email || '').toLowerCase().includes(f) || (user.uid || '').toLowerCase().includes(f);
    });

    return (
      <div>
        {this.state.isLoading && (
          <>
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9998 }}></div>
            <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9999, backgroundColor: 'rgba(33,33,33,0.95)', padding: '24px', borderRadius: '12px' }}>
              <CircularProgress size={40} style={{ color: '#4caf50' }} />
            </div>
          </>
        )}

        {/* CREATE MODAL */}
        <Dialog open={this.state.showCreateModal} onClose={this.handleCreateClose} fullWidth maxWidth="sm">
          <DialogTitle style={{ fontSize: '18px', fontWeight: 600 }}>Create New User</DialogTitle>
          <DialogContent>
            <div style={styles.dialogForm}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Name</label>
                <input style={styles.formInput} value={this.state.newUser.name} onChange={(e) => this.handleInputChange('name', e.target.value)} placeholder="User name" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Email</label>
                <input style={styles.formInput} type="email" value={this.state.newUser.email} onChange={(e) => this.handleInputChange('email', e.target.value)} placeholder="user@example.com" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Accessible Vaults</label>
                {this.renderVaultPicker(this.state.newUser.assigned_vault_ids, this.handleNewUserVaultToggle)}
              </div>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Selected Vault</label>
                  <select style={styles.formSelect} value={this.state.newUser.selected_vault_id} onChange={(e) => this.handleInputChange('selected_vault_id', e.target.value)}>
                    <option value="">None</option>
                    {this.state.vaults.map(v => <option key={v.id} value={v.id}>{escapeHtml(v.name)}</option>)}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Role</label>
                  <select style={styles.formSelect} value={this.state.newUser.role} onChange={(e) => this.handleInputChange('role', e.target.value)}>
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Shopping List ID</label>
                <input style={styles.formInput} type="number" value={this.state.newUser.selected_shopping_list_id} onChange={(e) => this.handleInputChange('selected_shopping_list_id', e.target.value)} placeholder="Optional" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.checkboxToggle}>
                  <input type="checkbox" checked={this.state.newUser.allowed} onChange={(e) => this.handleInputChange('allowed', e.target.checked)} style={{ accentColor: '#4caf50' }} />
                  <span>Allowed access</span>
                </label>
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <button className="btn btn-secondary" onClick={this.handleCreateClose}>Cancel</button>
            <button className="btn btn-primary" onClick={this.handleCreateUser}>Create</button>
          </DialogActions>
        </Dialog>

        {/* EDIT MODAL */}
        <Dialog open={this.state.showEditModal} onClose={this.handleEditClose} fullWidth maxWidth="sm">
          <DialogTitle style={{ fontSize: '18px', fontWeight: 600 }}>Edit User — {this.state.editingUser ? escapeHtml(this.state.editingUser.name || 'Unnamed') : ''}</DialogTitle>
          <DialogContent>
            <div style={styles.dialogForm}>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}><FontAwesomeIcon icon={faUser} style={{ marginRight: '4px', fontSize: '11px' }} /> Name</label>
                  <input style={styles.formInput} value={this.state.editingUser?.name || ''} onChange={(e) => this.handleEditInputChange('name', e.target.value)} placeholder="User name" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}><FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '4px', fontSize: '11px' }} /> Email</label>
                  <input style={styles.formInput} type="email" value={this.state.editingUser?.email || ''} onChange={(e) => this.handleEditInputChange('email', e.target.value)} placeholder="user@example.com" />
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}><FontAwesomeIcon icon={faKey} style={{ marginRight: '4px', fontSize: '11px' }} /> Auth UID</label>
                <input style={{ ...styles.formInput, fontFamily: 'monospace', fontSize: '12px' }} value={this.state.editingUser?.uid || ''} onChange={(e) => this.handleEditInputChange('uid', e.target.value)} placeholder="Auth UID" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}><FontAwesomeIcon icon={faLock} style={{ marginRight: '4px', fontSize: '11px' }} /> App Password</label>
                <input style={styles.formInput} type="password" autoComplete="new-password" value={this.state.editingUser?.password || ''} onChange={(e) => this.handleEditInputChange('password', e.target.value)} placeholder="Leave blank to keep current password" />
                <span style={{ fontSize: '11px', color: '#9e9e9e' }}>Sets the in-app login password for this user (min 6 chars).</span>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}><FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: '4px', fontSize: '11px' }} /> Accessible Vaults</label>
                {this.renderVaultPicker(this.state.editingUser?.assigned_vault_ids, this.handleEditVaultToggle)}
              </div>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Selected Vault</label>
                  <select style={styles.formSelect} value={this.state.editingUser?.selected_vault_id || ''} onChange={(e) => this.handleEditInputChange('selected_vault_id', e.target.value)}>
                    <option value="">None</option>
                    {this.state.vaults.map(v => <option key={v.id} value={v.id}>{escapeHtml(v.name)}</option>)}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Shopping List ID</label>
                  <input style={styles.formInput} type="number" value={this.state.editingUser?.selected_shopping_list_id || ''} onChange={(e) => this.handleEditInputChange('selected_shopping_list_id', e.target.value)} placeholder="Optional" />
                </div>
              </div>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Role</label>
                  {this.isAdmin()
                    ? <select style={styles.formSelect} value={this.state.editingUser?.role || 'user'} onChange={(e) => this.handleEditInputChange('role', e.target.value)}>
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    : <input style={{ ...styles.formInput, backgroundColor: '#1e1e1e', cursor: 'not-allowed' }} value={this.state.editingUser?.role || 'user'} disabled />
                  }
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.checkboxToggle}>
                    <input type="checkbox" checked={this.state.editingUser?.allowed || false} onChange={(e) => this.handleEditInputChange('allowed', e.target.checked)} style={{ accentColor: '#4caf50' }} />
                    <span>Allowed access</span>
                  </label>
                </div>
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <button className="btn btn-secondary" onClick={this.handleEditClose}>Cancel</button>
            <button className="btn btn-primary" onClick={this.handleUpdateUser}>Save Changes</button>
          </DialogActions>
        </Dialog>

        {/* DELETE MODAL */}
        <Dialog open={this.state.showDeleteModal} onClose={this.handleDeleteClose}>
          <DialogTitle style={{ color: '#f44336', fontSize: '18px', fontWeight: 600 }}>Delete User</DialogTitle>
          <DialogContent>
            <p style={{ color: '#e0e0e0' }}>Are you sure you want to permanently delete this user?</p>
            <div style={{ backgroundColor: '#2d2d2d', padding: '12px', borderRadius: '8px', marginTop: '8px' }}>
              <div style={{ color: '#ffffff', fontWeight: 600 }}>{escapeHtml(this.state.userToDelete?.name || 'N/A')}</div>
              <div style={{ color: '#9e9e9e', fontSize: '13px', fontFamily: 'monospace' }}>{escapeHtml(this.state.userToDelete?.uid || '')}</div>
            </div>
            <p style={{ color: '#f44336', fontSize: '12px', marginTop: '12px' }}><strong>Warning:</strong> This will remove the user account. Their vaults and data will not be deleted.</p>
          </DialogContent>
          <DialogActions>
            <button className="btn btn-secondary" onClick={this.handleDeleteClose}>Cancel</button>
            <button className="btn btn-danger" onClick={this.handleDeleteUser} style={{ backgroundColor: '#f44336' }}>Delete User</button>
          </DialogActions>
        </Dialog>

        {/* ASSIGN VAULT MODAL */}
        <Dialog open={this.state.showAssignVaultModal} onClose={this.handleAssignVaultClose}>
          <DialogTitle style={{ fontSize: '18px', fontWeight: 600 }}>Assign Vault</DialogTitle>
          <DialogContent>
            <p style={{ color: '#e0e0e0', marginBottom: '12px' }}>Assign a vault to <strong style={{ color: '#ffffff' }}>{escapeHtml(this.state.assigningUser?.name || 'N/A')}</strong></p>
            <select style={styles.formSelect} value={this.state.selectedVaultToAssign} onChange={(e) => this.setState({ selectedVaultToAssign: e.target.value })}>
              <option value="">Select a vault...</option>
              {this.state.vaults.map(v => <option key={v.id} value={v.id}>{escapeHtml(v.name)}</option>)}
            </select>
          </DialogContent>
          <DialogActions>
            <button className="btn btn-secondary" onClick={this.handleAssignVaultClose}>Cancel</button>
            <button className="btn btn-primary" onClick={() => this.handleAssignVault(this.state.selectedVaultToAssign)} disabled={!this.state.selectedVaultToAssign}>Assign</button>
          </DialogActions>
        </Dialog>

        {/* EMAIL LOOKUP MODAL */}
        <Dialog open={this.state.showLookupModal} onClose={this.handleLookupClose} fullWidth maxWidth="sm">
          <DialogTitle style={{ fontSize: '18px', fontWeight: 600 }}>Email Lookup</DialogTitle>
          <DialogContent>
            <div style={styles.dialogForm}>
              <p style={{ margin: '0 0 12px 0', color: '#9e9e9e', fontSize: '13px' }}>Search for a user by email, or pre-provision a new user for Authentik login.</p>
              <input style={styles.formInput} type="email" value={this.state.lookupEmail} onChange={(e) => this.setState({ lookupEmail: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') this.handleLookupSearch(); }} placeholder="Enter email address..." />
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button style={{ ...styles.btnSecondary, flex: 1 }} onClick={this.handleLookupSearch}><FontAwesomeIcon icon={faSearch} /> Search</button>
                {this.isAdmin() && <button style={{ ...styles.btnPrimary, flex: 1, backgroundColor: '#ff9800' }} onClick={this.handleLookupCreate}><FontAwesomeIcon icon={faPlus} /> Create</button>}
              </div>
              {this.state.lookupError && <div style={{ color: '#f44336', fontSize: '13px', marginTop: '8px' }}>{escapeHtml(this.state.lookupError)}</div>}
              {this.state.lookupResult && this.state.lookupResult.found && (
                <div style={styles.lookupResult('found')}>
                  <div style={styles.lookupResultTitle('found')}>✓ User Found</div>
                  <div style={styles.lookupResultRow}><strong>Name:</strong> {escapeHtml(this.state.lookupResult.name || 'N/A')}</div>
                  <div style={styles.lookupResultRow}><strong>Email:</strong> {escapeHtml(this.state.lookupResult.email)}</div>
                  <div style={styles.lookupResultRow}><strong>UID:</strong> <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{escapeHtml(this.state.lookupResult.uid)}</span></div>
                  <div style={styles.lookupResultRow}><strong>Role:</strong> {escapeHtml(this.state.lookupResult.role || 'user')}</div>
                  <div style={styles.lookupResultRow}><strong>Status:</strong> <span style={{ color: this.state.lookupResult.allowed ? '#4caf50' : '#f44336' }}>{this.state.lookupResult.allowed ? 'Active' : 'Pending'}</span></div>
                </div>
              )}
              {this.state.lookupResult && !this.state.lookupResult.found && this.state.lookupResult.created && (
                <div style={styles.lookupResult('created')}>
                  <div style={styles.lookupResultTitle('created')}>✓ User Created</div>
                  <div style={styles.lookupResultRow}><strong>Name:</strong> {escapeHtml(this.state.lookupResult.name || 'N/A')}</div>
                  <div style={styles.lookupResultRow}><strong>Email:</strong> {escapeHtml(this.state.lookupResult.email)}</div>
                  <div style={styles.lookupResultRow}><strong>UID:</strong> <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{escapeHtml(this.state.lookupResult.uid)}</span></div>
                  <div style={{ marginTop: '8px', color: '#4caf50', fontSize: '13px' }}>Ready for Authentik login.</div>
                </div>
              )}
              {this.state.lookupResult && !this.state.lookupResult.found && !this.state.lookupResult.created && (
                <div style={styles.lookupResult('error')}>
                  <div style={styles.lookupResultTitle('error')}>✗ No User Found</div>
                  <div style={styles.lookupResultRow}>No user with email <strong>{escapeHtml(this.state.lookupResult.email)}</strong> exists.
                    {this.isAdmin() && <div style={{ marginTop: '6px', color: '#ff9800' }}>Click "Create" to pre-provision this user.</div>}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <button className="btn btn-secondary" onClick={this.handleLookupClose}>Close</button>
          </DialogActions>
        </Dialog>

        {/* MAIN PAGE */}
        <div className='app-background'>
          <div className='main-shopping-container'>
            <div className='main-item-container'>
              <div style={styles.pageHeader}>
                <h2 style={styles.pageTitle}>User Management</h2>
                <div style={styles.headerActions}>
                  <input style={styles.searchInput} value={this.state.searchFilter} onChange={(e) => this.handleSearchFilterChange(e.target.value)} placeholder="Search users..." />
                  {this.isAdmin() && (
                    <>
                      <button style={styles.btnLookup} onClick={this.handleLookupOpen}><FontAwesomeIcon icon={faSearch} /> Email Lookup</button>
                      <button style={styles.btnPrimary} onClick={this.handleCreateOpen}><FontAwesomeIcon icon={faPlus} /> Add User</button>
                    </>
                  )}
                </div>
              </div>
              <div>
                {filteredUsers.length === 0 ? (
                  <div style={styles.emptyState}>
                    {this.state.searchFilter
                      ? <><FontAwesomeIcon icon={faSearch} style={{ fontSize: '32px', marginBottom: '12px', display: 'block' }} /><div>No users match "{escapeHtml(this.state.searchFilter)}"</div></>
                      : <><FontAwesomeIcon icon={faUser} style={{ fontSize: '32px', marginBottom: '12px', display: 'block' }} /><div>No users found</div></>}
                  </div>
                ) : filteredUsers.map(user => this.renderUserCard(user))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default UserManagement;
